import { useRef } from "react";
import { ArrowRight, BatteryFull, Check, Landmark, Loader2, MapPin, ShieldCheck, Signal, Upload, Wifi } from "lucide-react";
import { LogoMark } from "@/components/agent/AgentLayout";
import { FlightPath, resetFlight } from "@/components/applicant/FlightPath";
import { PassLabel } from "@/components/applicant/JourneyPanel";
import { QuestionStep, StepHeader, StepTitle } from "@/components/applicant/QuestionStep";
import { CheckList } from "@/components/applicant/ReviewStage";
import { QUESTIONS } from "@/components/applicant/questions";
import { PassBox } from "@/components/applicant/stageUi";
import { buttonVariants } from "@/components/ui/button";
import { gbp } from "@/lib/format";
import { cn } from "@/lib/utils";
import { FADE_OUT, Scaled, Tap, between } from "@/components/landing/stage";
import { AGENCY, PRESS_MS, PROPERTY, TENANT as T } from "@/components/landing/script";

/** The tenant's application on a phone at real size (390 × 844), then shrunk to fit (see Scaled).
 *  Uses the applicant flow's own components where they don't need the server; the rest mirrors their markup. */
const W = 390;
const H = 844;

/** The route the plane flies in this demo: one stop per section, as in the real flow. */
const STEPS = [
  { key: "photo_id", section: "Your ID" },
  { key: "pay_type", section: "Your income" },
  { key: "payslips", section: "Your income" },
  { key: "bank_statements", section: "Your income" },
  { key: "review", section: "Check and send" },
];
const INDEX = { id: 0, pay: 1, payslips: 2, bank: 3, review: 4 };
const MONTHS = ["July 2026", "August 2026", "September 2026"];
const PAY = QUESTIONS.find((q) => q.key === "pay_type");

const sceneAt = (t) =>
  t < T.id ? "intro" : t < T.pay ? "id" : t < T.payslips ? "pay" : t < T.bank ? "payslips" : t < T.review ? "bank" : t < T.done ? "review" : "done";

const pressed = (t, at) => between(t, at, at + PRESS_MS);

/** A full-width button, "tapped" by the script. */
const Action = ({ children, down, variant }) => (
  <span className={cn(buttonVariants({ size: "xl", variant }), "relative w-full overflow-hidden transition-transform", down && "scale-[0.98] opacity-90")}>
    {children}
    <Tap on={down} />
  </span>
);

const BoardingPass = () => (
  <div className="relative mt-8 overflow-hidden rounded-3xl border bg-background">
    <div className="px-5 pb-4 pt-5">
      <div className="flex items-start justify-between gap-6">
        <div>
          <PassLabel>From</PassLabel>
          <p className="mt-1 text-base font-semibold">Today</p>
        </div>
        <div className="min-w-0 text-right">
          <PassLabel>To</PassLabel>
          <p className="mt-1 text-base font-semibold leading-snug">Flat 2, 14 Mare Street<span className="block text-sm font-normal text-muted-foreground">London E8</span></p>
        </div>
      </div>
      <FlightPath t={0} stops={[0.25, 0.5, 0.75]} className="mt-3" testid="landing-intro-flight" />
    </div>
    <div className="relative border-t border-dashed">
      <span className="absolute -left-3 -top-3 size-6 rounded-full border bg-panel" />
      <span className="absolute -right-3 -top-3 size-6 rounded-full border bg-panel" />
    </div>
    <dl className="grid grid-cols-2 gap-4 px-5 py-4">
      <div><dt><PassLabel>Rent</PassLabel></dt><dd className="mt-1 text-sm font-medium">{gbp(PROPERTY.rent)} a month</dd></div>
      <div className="text-right"><dt><PassLabel>Move in</PassLabel></dt><dd className="mt-1 text-sm font-medium">{PROPERTY.moveIn}</dd></div>
    </dl>
  </div>
);

const Accepted = () => (
  <span className="mb-3 inline-flex items-center gap-1 rounded-full bg-success/10 px-2.5 py-1 text-xs font-medium text-success"><Check className="size-3" strokeWidth={3} /> Accepted</span>
);

const Where = ({ children }) => (
  <div className="mt-5 flex gap-3 rounded-xl bg-background p-4 text-sm leading-relaxed ring-1 ring-border">
    <MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
    <p><span className="font-medium">Where to find it. </span>{children}</p>
  </div>
);

const DropZone = ({ down, multi }) => (
  <div className={cn("relative mt-6 flex w-full flex-col items-center gap-3 overflow-hidden rounded-2xl border-[1.5px] border-dashed border-muted-foreground/35 bg-background px-6 py-9 text-center transition-transform", down && "scale-[0.99]")}>
    <span className="grid size-12 place-items-center rounded-full bg-brand text-brand-foreground"><Upload className="size-5" /></span>
    <span className="text-base font-semibold">Upload</span>
    <span className="text-sm text-muted-foreground">PDF or photo.{multi ? " You can pick several at once." : ""}</span>
    <Tap on={down} />
  </div>
);

const Checking = ({ name }) => (
  <div className="mt-6 rounded-xl bg-brand/40 p-4 dark:bg-brand/10">
    <p className="flex items-center gap-2 text-sm font-medium"><Loader2 className="size-4 animate-spin" /> Checking {name}…</p>
    <div className="scan-bar relative mt-3 h-px overflow-hidden" />
    <p className="mt-3 text-xs text-muted-foreground">This usually takes a few seconds.</p>
  </div>
);

const Months = ({ covered }) => (
  <div className="mt-3 flex flex-wrap gap-1.5">
    {MONTHS.map((m, i) => (
      <span key={m} className={cn("inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors", i < covered ? "bg-success/10 text-success" : "bg-muted text-muted-foreground")}>
        {i < covered && <Check className="size-3" strokeWidth={3} />} {m}
      </span>
    ))}
  </div>
);

const Tracker = () => (
  <ol className="mt-8">
    {[["Submitted", "done"], ["In review", "current"], ["Decision", "next"]].map(([label, state], i) => (
      <li key={label} className="relative flex gap-3 pb-6 last:pb-0">
        {i < 2 && <span className={cn("absolute left-[11px] top-6 h-[calc(100%-24px)] w-0.5", state === "done" ? "bg-success" : "bg-border")} />}
        <span className={cn("relative grid size-6 shrink-0 place-items-center rounded-full",
          state === "done" ? "bg-success text-white" : state === "current" ? "border-2 border-foreground bg-background" : "border-2 border-border bg-background")}>
          {state === "done" && <Check className="size-3.5" strokeWidth={3} />}
        </span>
        <div className="pt-0.5">
          <p className={cn("text-base", state === "next" ? "text-muted-foreground" : "font-semibold")}>{label}</p>
          {state === "done" && <p className="text-xs text-muted-foreground">Just now</p>}
          {state === "current" && <p className="text-xs text-muted-foreground">Waiting for the agent to open it</p>}
        </div>
      </li>
    ))}
  </ol>
);

/** What the screen shows and which button sits at the bottom, at time `t`. */
const screenAt = (scene, t) => {
  if (scene === "intro")
    return {
      body: (
        <div className="rise">
          <PassLabel>{AGENCY} · Rental application</PassLabel>
          <h1 className="mt-3 text-[30px] font-semibold leading-[1.1] tracking-tight">Let's get you into your new home</h1>
          <p className="mt-3 text-base leading-relaxed text-muted-foreground">A few short stops. Each document is checked in seconds, so you'll know straight away if anything needs fixing.</p>
          <BoardingPass />
        </div>
      ),
      action: <Action down={pressed(t, T.start)}>Start <ArrowRight /></Action>,
    };
  if (scene === "id") {
    const checking = between(t, T.idUpload + 150, T.idChecked);
    const done = t >= T.idChecked;
    return {
      body: (
        <div className="rise">
          {done && <Accepted />}
          <h1 className="text-[26px] font-semibold leading-tight tracking-tight">Your passport or photo driving licence</h1>
          <p className="mt-4 text-base font-medium">An in-date passport or UK photo driving licence</p>
          {!done && <Where>Take a clear photo of the photo page, flat and in good light, or upload a scan.</Where>}
          {done && <div className="mt-6"><PassBox>Passport · Jane Smith-Jones · in date until 14 Mar 2031</PassBox></div>}
          {checking && <Checking name="passport.jpg" />}
          {!done && !checking && <DropZone down={pressed(t, T.idUpload)} />}
        </div>
      ),
      action: done && <Action down={pressed(t, T.idNext)}>Continue</Action>,
    };
  }
  if (scene === "pay") return { body: <QuestionStep q={PAY} current={t >= T.payPick ? "employed" : undefined} onAnswer={() => {}} /> };
  if (scene === "payslips") {
    const covered = T.slipChecked.filter((at) => t >= at).length;
    const checking = between(t, T.slipUpload + 150, T.slipChecked[2]);
    const done = covered === MONTHS.length;
    return {
      body: (
        <div className="rise">
          {done && <Accepted />}
          <h1 className="text-[26px] font-semibold leading-tight tracking-tight">Your payslips</h1>
          <p className="mt-4 text-base font-medium">Payslips for July, August and September 2026</p>
          <Months covered={covered} />
          {!done && !checking && <Where>Download them as PDFs from your employer's payroll portal, or ask your HR or payroll team to email them.</Where>}
          {done && <div className="mt-6"><PassBox>3 payslips · Acme Ltd · take-home £3,120 a month</PassBox></div>}
          {checking && <Checking name={`payslip-${["july", "august", "september"][covered]}.pdf`} />}
          {!done && !checking && <DropZone multi down={pressed(t, T.slipUpload)} />}
        </div>
      ),
      action: done && <Action down={pressed(t, T.slipNext)}>Continue</Action>,
    };
  }
  if (scene === "bank") {
    const connecting = between(t, T.bankConnect, T.bankChecking);
    const checking = between(t, T.bankChecking, T.bankChecked);
    const done = t >= T.bankChecked;
    return {
      body: (
        <div className="rise">
          <StepTitle sub="Now let's check your pay arrives in your bank, for July, August and September 2026.">Connect your bank</StepTitle>
          <div className="mt-6 flex items-start gap-3 rounded-xl bg-background p-4 ring-1 ring-border">
            <ShieldCheck className="mt-0.5 size-5 shrink-0 text-success" />
            <p className="text-base font-medium leading-snug">We only look for your pay. The agent never sees your spending.</p>
          </div>
          {checking && <p className="mt-4 flex items-center gap-2 rounded-lg bg-brand/40 p-3 text-sm font-medium dark:bg-brand/10"><Loader2 className="size-4 animate-spin" /> Checking your payments…</p>}
          {done && <div className="mt-4"><PassBox><span className="font-medium">Found 3 payments from Acme Ltd matching your payslips</span></PassBox></div>}
        </div>
      ),
      action: done ? <Action down={pressed(t, T.bankNext)}>Continue</Action> : (
        <Action down={pressed(t, T.bankConnect)}>{connecting ? <Loader2 className="animate-spin" /> : <Landmark />} Connect your bank</Action>
      ),
    };
  }
  if (scene === "review") {
    const ticked = (i) => (t >= T.review + 250 + i * 180 ? "pass" : "todo");
    return {
      body: (
        <div className="rise">
          <StepTitle sub={`Everything checked out. One last look, then send it to ${AGENCY}.`}>Final check</StepTitle>
          <h2 className="mb-3 mt-8 text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">Done and checked</h2>
          <CheckList testid="landing-review-checks" checks={[
            { key: "id", label: "Your passport or photo driving licence", status: ticked(0) },
            { key: "pay", label: "How you're paid", detail: "Employed", status: ticked(1) },
            { key: "slips", label: "Your payslips", detail: "July, August and September 2026", status: ticked(2) },
            { key: "bank", label: "Connect your bank", detail: "Pay matches your payslips", status: ticked(3) },
          ]} />
        </div>
      ),
      action: <Action down={pressed(t, T.send)}>Send to {AGENCY}</Action>,
    };
  }
  return {
    body: (
      <div className="rise">
        <FlightPath landed stops={[0.25, 0.5, 0.75]} testid="landing-done-flight" />
        <p className="mt-6 text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">Sent to {AGENCY}</p>
        <h1 className="mt-2 text-[30px] font-semibold leading-[1.1] tracking-tight">You're nearly home</h1>
        <p className="mt-3 text-base leading-relaxed text-muted-foreground">Thank you. {AGENCY} can now review your application for {PROPERTY.address}. They usually decide within a day.</p>
        <Tracker />
      </div>
    ),
  };
};

/** Right side of the landing page: a tenant applying on their phone, from the boarding pass to landing at the new home. */
export const TenantDemo = ({ t, className }) => {
  const scene = sceneAt(t);
  // The flight path remembers where its plane was; when the loop restarts, start the boarding pass fresh rather than flying back.
  const lastT = useRef(t);
  if (t < lastT.current) resetFlight();
  lastT.current = t;

  const { body, action } = screenAt(scene, t);
  const index = INDEX[scene];
  const accepted = (scene === "id" && t >= T.idChecked) || (scene === "payslips" && t >= T.slipChecked[2]) || (scene === "bank" && t >= T.bankChecked) || scene === "review";
  return (
    <PhoneFrame className={className} testid="landing-tenant-demo">
      <header className="shrink-0 border-b bg-background">
        <div className="flex h-14 items-center justify-between px-5">
          <span className="inline-flex items-center gap-2"><LogoMark /><span className="text-sm font-semibold">Landin</span></span>
          <span className="truncate pl-4 text-xs text-muted-foreground">{AGENCY}</span>
        </div>
      </header>
      <main className={cn("relative min-h-0 flex-1 overflow-hidden px-5 pt-6 transition-opacity duration-300", t >= FADE_OUT || t < 250 ? "opacity-0" : "opacity-100")}>
        {index !== undefined && (
          <StepHeader steps={STEPS} index={index} onBack={() => {}} phase={accepted ? 0.6 : 0} />
        )}
        <div key={scene}>{body}</div>
        {action && (
          <div className="absolute inset-x-0 bottom-0 flex flex-col gap-2 bg-gradient-to-t from-panel from-70% to-transparent px-5 pb-9 pt-6">{action}</div>
        )}
      </main>
    </PhoneFrame>
  );
};

/** A phone with a status bar, camera island and home bar. `children` fill the 390 × 794 screen below the status bar. */
export const PhoneFrame = ({ className, testid, children }) => (
  <div className={cn("mx-auto w-full max-w-[300px] rounded-[48px] bg-neutral-900 p-[10px] shadow-[0_30px_80px_-30px_rgb(0_0_0/0.45)] ring-1 ring-black/5 dark:ring-white/10", className)} data-testid={testid}>
    <div className="overflow-hidden rounded-[38px] bg-panel">
      <Scaled width={W} height={H}>
        <div className="relative flex h-full flex-col bg-panel text-sm text-foreground">
          <div className="flex h-[50px] shrink-0 items-center justify-between bg-background px-8 pt-1.5 text-[15px] font-semibold">
            <span>9:41</span>
            <span className="flex items-center gap-1.5"><Signal className="size-4" /><Wifi className="size-4" /><BatteryFull className="size-5" /></span>
          </div>
          <span className="absolute left-1/2 top-[11px] h-[30px] w-[110px] -translate-x-1/2 rounded-full bg-black" />
          {children}
          <span className="absolute bottom-2 left-1/2 h-[5px] w-[134px] -translate-x-1/2 rounded-full bg-foreground/80" />
        </div>
      </Scaled>
    </div>
  </div>
);
